import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { AlertCircle, X } from "lucide-react";
import { detectChromiumBrowser } from "@/lib/is-chromium-browser";
import {
  dismissBrowserCompatBanner,
  isBrowserCompatBannerDismissed,
} from "@/lib/browser-compat-banner-storage";
import { cn } from "@/lib/utils";

const EDGE_URL = "https://www.microsoft.com/edge";
const CHROME_URL = "https://www.google.com/chrome/";
const BRAVE_URL = "https://brave.com/download/";

export const BROWSER_COMPAT_BANNER_HEIGHT_VAR = "--browser-compat-banner-height";

const bannerLineClassName = "whitespace-nowrap pr-12";

function clearBannerHeightVar() {
  document.documentElement.style.setProperty(
    BROWSER_COMPAT_BANNER_HEIGHT_VAR,
    "0px",
  );
}

function BrowserCompatBannerMessage() {
  return (
    <>
      Estás utilizando un <strong>navegador</strong> que no es 100% compatible
      con esta plataforma. Te recomendamos utilizar{" "}
      <a
        href={EDGE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="font-semibold underline underline-offset-2"
      >
        Microsoft Edge
      </a>
      ,{" "}
      <a
        href={CHROME_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="font-semibold underline underline-offset-2"
      >
        Google Chrome
      </a>{" "}
      o{" "}
      <a
        href={BRAVE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="font-semibold underline underline-offset-2"
      >
        Brave
      </a>
      .
    </>
  );
}

export function UnsupportedBrowserBanner() {
  const bannerRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLParagraphElement>(null);
  const [dismissedForSession, setDismissedForSession] = useState(false);
  const [dismissedPermanently, setDismissedPermanently] = useState(
    () => isBrowserCompatBannerDismissed(),
  );
  const [marquee, setMarquee] = useState(false);
  const [marqueeDurationSec, setMarqueeDurationSec] = useState(24);

  const showBanner =
    !detectChromiumBrowser() && !dismissedForSession && !dismissedPermanently;

  const hideBanner = () => {
    clearBannerHeightVar();
  };

  const handleDismissSession = () => {
    hideBanner();
    setDismissedForSession(true);
  };

  const handleDismissPermanent = () => {
    dismissBrowserCompatBanner();
    hideBanner();
    setDismissedPermanently(true);
  };

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const line = lineRef.current;
    if (!showBanner || !viewport || !line) {
      setMarquee(false);
      return;
    }

    const syncMarquee = () => {
      const overflow = line.scrollWidth > viewport.clientWidth;
      setMarquee(overflow);
      if (overflow) {
        setMarqueeDurationSec(Math.max(14, line.scrollWidth / 45));
      }
    };

    syncMarquee();
    const observer = new ResizeObserver(syncMarquee);
    observer.observe(viewport);
    observer.observe(line);
    return () => observer.disconnect();
  }, [showBanner]);

  useLayoutEffect(() => {
    const el = bannerRef.current;
    if (!showBanner || !el) {
      clearBannerHeightVar();
      return;
    }

    const syncHeight = () => {
      document.documentElement.style.setProperty(
        BROWSER_COMPAT_BANNER_HEIGHT_VAR,
        `${el.offsetHeight}px`,
      );
    };

    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(el);

    return () => {
      observer.disconnect();
      clearBannerHeightVar();
    };
  }, [showBanner, marquee]);

  if (!showBanner) return null;

  return (
    <div
      ref={bannerRef}
      role="alert"
      className="fixed inset-x-0 top-0 z-50 flex w-full items-center gap-3 bg-red-600 px-4 py-2.5 text-sm text-white"
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white">
        <AlertCircle className="h-4 w-4 text-red-600" aria-hidden />
      </span>
      <div
        ref={viewportRef}
        className="min-w-0 flex-1 overflow-hidden motion-reduce:overflow-x-auto"
      >
        <div
          className={cn("flex w-max", marquee && "browser-banner-marquee")}
          style={
            marquee
              ? ({
                  "--browser-banner-marquee-duration": `${marqueeDurationSec}s`,
                } as CSSProperties)
              : undefined
          }
        >
          <p ref={lineRef} className={bannerLineClassName}>
            <BrowserCompatBannerMessage />
          </p>
          {marquee ? (
            <p className={bannerLineClassName} aria-hidden>
              <BrowserCompatBannerMessage />
            </p>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={handleDismissPermanent}
          className="whitespace-nowrap text-xs font-medium underline underline-offset-2 hover:text-white/90"
        >
          No volver a mostrar
        </button>
        <button
          type="button"
          onClick={handleDismissSession}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/15"
          aria-label="Cerrar aviso"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}
